import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import type { TourDefinition, TourPlacement } from './types';
import { Button, IconButton } from '@/components/ds-zta';
import { shouldHandleTourKey } from './tourKeys';

interface TourOverlayProps {
  tour: TourDefinition;
  stepIndex: number;
  onNext: () => void;
  onPrev: () => void;
  onPause: () => void;
  onFinish: () => void;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const SPOTLIGHT_PADDING = 8;
const TOOLTIP_WIDTH = 340;
const GAP = 14; // space between the spotlight and the tooltip
const VIEWPORT_MARGIN = 12;

// Two rects are equal within half a pixel — used to skip no-op state updates.
function sameRect(a: Rect | null, b: Rect | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  );
}

// Resolve a target element and keep its rect in sync with scroll/resize.
//
// Tracking is deliberately NOT a perpetual requestAnimationFrame loop (that kept
// the CPU busy and left the page feeling "hung" after the tour closed). Instead:
//   • a bounded rAF poll finds the element (it may still be mounting after a route
//     change) — capped at ~3s,
//   • once found we scroll it into view and re-measure on scroll/resize via
//     listeners, plus a short bounded rAF burst to follow the smooth-scroll
//     animation, then everything goes idle.
// Every timer/listener is torn down on cleanup, so nothing survives the overlay.
function useTargetRect(selector: string | undefined, stepIndex: number) {
  const [rect, setRect] = useState<Rect | null>(null);
  const [found, setFound] = useState(false);
  const [failed, setFailed] = useState(false);
  const rectRef = useRef<Rect | null>(null);

  useEffect(() => {
    rectRef.current = null;
    setRect(null);
    setFound(false);
    setFailed(false);

    if (!selector) {
      setFound(true); // centered step — nothing to wait for
      return;
    }

    let cancelled = false;
    let pollRaf = 0;
    let burstRaf = 0;
    let attempts = 0;
    let framesLeft = 0;
    let el: Element | null = null;

    const measure = () => {
      if (cancelled || !el) return;
      const r = el.getBoundingClientRect();
      const next: Rect = { top: r.top, left: r.left, width: r.width, height: r.height };
      if (!sameRect(rectRef.current, next)) {
        rectRef.current = next;
        setRect(next);
      }
    };

    // Bounded burst that follows the smooth-scroll animation, then stops.
    const burst = () => {
      if (cancelled) return;
      measure();
      if (framesLeft-- > 0) burstRaf = requestAnimationFrame(burst);
    };

    const onScrollOrResize = () => {
      measure();
    };

    const findTick = () => {
      if (cancelled) return;
      const node = document.querySelector(selector);
      if (node) {
        el = node;
        setFound(true);
        node.scrollIntoView({ block: 'center', behavior: 'smooth' });
        measure();
        framesLeft = 45; // ~0.75s of follow frames, then idle
        burstRaf = requestAnimationFrame(burst);
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        return;
      }
      if (attempts < 180) {
        attempts += 1;
        pollRaf = requestAnimationFrame(findTick);
      } else {
        setFailed(true); // gave up — the overlay falls back to a centered card
      }
    };

    pollRaf = requestAnimationFrame(findTick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(pollRaf);
      cancelAnimationFrame(burstRaf);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selector, stepIndex]);

  return { rect, found, failed };
}

// Decide which side the tooltip sits on and where, clamped to the viewport.
function computeTooltipPosition(
  rect: Rect,
  preferred: TourPlacement | undefined,
  tooltipHeight: number,
): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const space = {
    top: rect.top,
    bottom: vh - (rect.top + rect.height),
    left: rect.left,
    right: vw - (rect.left + rect.width),
  };

  const order: TourPlacement[] = [preferred ?? 'bottom', 'bottom', 'top', 'right', 'left'];
  const fits = (p: TourPlacement) => {
    if (p === 'bottom') return space.bottom >= tooltipHeight + GAP;
    if (p === 'top') return space.top >= tooltipHeight + GAP;
    if (p === 'right') return space.right >= TOOLTIP_WIDTH + GAP;
    if (p === 'left') return space.left >= TOOLTIP_WIDTH + GAP;
    return false;
  };
  const placement = order.find(fits) ?? 'bottom';

  let top = 0;
  let left = 0;
  switch (placement) {
    case 'bottom':
      top = rect.top + rect.height + GAP;
      left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
      break;
    case 'top':
      top = rect.top - tooltipHeight - GAP;
      left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.left + rect.width + GAP;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.left - TOOLTIP_WIDTH - GAP;
      break;
    default:
      break;
  }

  left = Math.min(Math.max(left, VIEWPORT_MARGIN), vw - TOOLTIP_WIDTH - VIEWPORT_MARGIN);
  top = Math.min(Math.max(top, VIEWPORT_MARGIN), vh - tooltipHeight - VIEWPORT_MARGIN);
  return { top, left };
}


const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Keep Tab and Shift+Tab cycling inside the card while the tour is open.
function trapTab(e: KeyboardEvent, card: HTMLElement | null) {
  if (!card) return;
  const items = Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE));
  if (items.length === 0) return;
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  if (e.shiftKey && (active === first || !card.contains(active))) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && (active === last || !card.contains(active))) {
    e.preventDefault();
    first.focus();
  }
}

// One dim panel of the backdrop. Four of these frame the spotlight cut-out —
// plain divs (no SVG, no huge box-shadow), so nothing composites into a stale
// band when the overlay unmounts.
function DimPanel({ style }: { style: React.CSSProperties }) {
  return <div className="rc-tour__dim pointer-events-none fixed bg-[var(--overlay-scrim)]" style={style} />;
}

export default function TourOverlay({
  tour,
  stepIndex,
  onNext,
  onPrev,
  onPause,
  onFinish,
}: TourOverlayProps) {
  const step = tour.steps[stepIndex];
  const total = tour.steps.length;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === total - 1;
  const padding = step.spotlightPadding ?? SPOTLIGHT_PADDING;

  const { rect, failed } = useTargetRect(step.target, stepIndex);

  // Direction of travel, so the step content slides in from the side it came from.
  const prevIndexRef = useRef(stepIndex);
  const direction = stepIndex < prevIndexRef.current ? 'back' : 'forward';
  useEffect(() => {
    prevIndexRef.current = stepIndex;
  }, [stepIndex]);

  // The card's height follows its content (measured by a ResizeObserver) and is
  // set explicitly, so a change of copy between steps animates instead of snapping.
  const contentRef = useRef<HTMLDivElement>(null);
  const [tooltipHeight, setTooltipHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const node = contentRef.current;
    if (!node) return;
    setTooltipHeight(node.offsetHeight);
    const ro = new ResizeObserver(() => setTooltipHeight(node.offsetHeight));
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  // Transitions switch on one frame after the first paint, so the card and the
  // spotlight appear in place rather than gliding in from 0,0.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Centered steps are positioned from the viewport, so follow resizes.
  const [viewport, setViewport] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Focus (eval F43): the overlay is an aria-modal dialog, so focus moves into
  // it when it opens and goes back to wherever it was when it closes. Each new
  // step focuses its heading, unless focus is already on a control inside the
  // card (Next/Back keep focus, so paging with Enter stays one key).
  const rootRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    return () => {
      if (previous && previous.isConnected && typeof previous.focus === 'function') {
        previous.focus({ preventScroll: true });
      }
    };
  }, []);
  useEffect(() => {
    const active = document.activeElement;
    const card = cardRef.current;
    if (card && active && active !== document.body && card.contains(active)) return;
    headingRef.current?.focus({ preventScroll: true });
  }, [stepIndex]);

  // Keyboard: Esc pauses (keeps progress), arrows navigate, Tab stays inside
  // the card. Only while focus is in the tour and not in a text field, so the
  // keys never page the tour while someone types elsewhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!shouldHandleTourKey(e.target, rootRef.current)) return;
      if (e.key === 'Escape') onPause();
      else if (e.key === 'ArrowRight') onNext();
      else if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'Tab') trapTab(e, cardRef.current);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onNext, onPrev, onPause]);

  const height = tooltipHeight ?? 200;
  const wantsCenter = step.placement === 'center' || !step.target || failed;

  // A centered step is a zero-size "spotlight" in the middle of the screen: the
  // four dim panels then cover everything, and moving to an anchored step grows
  // the cut-out from the centre out to its target instead of popping in.
  const centerSpot: Rect = {
    top: Math.round(viewport.h / 2),
    left: Math.round(viewport.w / 2),
    width: 0,
    height: 0,
  };
  const centerPos = {
    top: Math.max((viewport.h - height) / 2, VIEWPORT_MARGIN),
    left: Math.max((viewport.w - TOOLTIP_WIDTH) / 2, VIEWPORT_MARGIN),
  };

  // While the next step's target is still resolving (route change, mount), hold
  // the previous spotlight and card position so nothing jumps to the corner.
  const lastSpotRef = useRef<Rect>(centerSpot);
  const lastPosRef = useRef(centerPos);

  let spot: Rect;
  let pos: { top: number; left: number };
  if (wantsCenter) {
    spot = centerSpot;
    pos = centerPos;
  } else if (rect) {
    spot = {
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    };
    pos = computeTooltipPosition(rect, step.placement, height);
  } else {
    spot = lastSpotRef.current;
    pos = lastPosRef.current;
  }
  lastSpotRef.current = spot;
  lastPosRef.current = pos;

  const hasCutout = spot.width > 0 && spot.height > 0;

  return createPortal(
    <div
      ref={rootRef}
      className="rc-tour fixed inset-0 z-[9998]"
      data-ready={ready ? '' : undefined}
      role="dialog"
      aria-modal="true"
      aria-live="polite"
      aria-label={step.title}
      // Read by scripts/verify/screenshots.mjs `tour`, which checks that each
      // step's target exists in the shell or page it spotlights.
      data-tour-target={step.target ?? ''}
    >
      {/* Backdrop dim. Spotlight = four plain panels around the cut-out (no SVG /
          box-shadow / filters → nothing composites into a stale band on unmount).
          Their edges transition together, so the cut-out glides between steps. */}
      <DimPanel style={{ top: 0, left: 0, right: 0, height: Math.max(spot.top, 0) }} />
      <DimPanel style={{ top: spot.top + spot.height, left: 0, right: 0, bottom: 0 }} />
      <DimPanel style={{ top: spot.top, left: 0, width: Math.max(spot.left, 0), height: spot.height }} />
      <DimPanel style={{ top: spot.top, left: spot.left + spot.width, right: 0, height: spot.height }} />
      {/* Highlight ring around the cut-out. */}
      <div
        className="rc-tour__ring pointer-events-none fixed rounded-xl ring-2 ring-[var(--brand)]/70"
        data-visible={hasCutout ? '' : undefined}
        style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height }}
      />

      {/* Click-catcher so the app underneath isn't interactable during the tour. */}
      <div className="fixed inset-0" onClick={onPause} />

      <div
        ref={cardRef}
        onClick={(e) => e.stopPropagation()}
        className="rc-tour__card fixed z-[9999] overflow-hidden rounded-2xl border border-[var(--d2d-border)] bg-[var(--d2d-bg-card)] shadow-2xl"
        style={{
          top: pos.top,
          left: pos.left,
          width: TOOLTIP_WIDTH,
          height: tooltipHeight ?? undefined,
        }}
      >
        <div ref={contentRef} className="p-5">
          <div
            className="rc-tour__progress"
            role="progressbar"
            aria-label="Tour progress"
            aria-valuemin={1}
            aria-valuemax={total}
            aria-valuenow={stepIndex + 1}
          >
            {tour.steps.map((s, i) => (
              <span
                key={s.title + i}
                className="rc-tour__seg"
                data-state={i < stepIndex ? 'done' : i === stepIndex ? 'current' : 'todo'}
              >
                <span className="rc-tour__seg-fill" />
              </span>
            ))}
          </div>

          <div className="mt-4 flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-[var(--ds-color-bg-subtle)] text-[var(--d2d-text-primary)]">
                <tour.icon className="h-[18px] w-[18px]" />
              </span>
              <div className="flex flex-col leading-tight">
                <span className="text-label font-semibold uppercase text-[var(--d2d-text-muted)]">
                  {tour.title}
                </span>
                <span className="text-micro text-[var(--d2d-text-muted)] tabular-nums">
                  Step {stepIndex + 1} of {total}
                </span>
              </div>
            </div>
            <IconButton
              aria-label="Pause tour (resume later from Getting Started)"
              size="sm"
              onClick={onPause}
              icon={<X className="h-4 w-4" />}
            />
          </div>

          {/* Keyed by step: remounts per step, which replays the slide-in. */}
          <div key={stepIndex} className="rc-tour__body" data-dir={direction}>
            <h3
              ref={headingRef}
              tabIndex={-1}
              className="mt-4 text-base font-medium text-[var(--d2d-text-primary)] focus:outline-none"
            >
              {step.title}
            </h3>
            <p className="mt-1.5 text-sm leading-relaxed text-[var(--d2d-text-secondary)]">{step.content}</p>
          </div>

          <div className="mt-5 flex items-center justify-between gap-2">
            <Button variant="ghost" size="sm" onClick={onPause}>
              Pause
            </Button>
            <div className="flex items-center gap-2">
              {!isFirst && (
                <Button variant="secondary" size="sm" onClick={onPrev}>
                  <ChevronLeft className="h-3.5 w-3.5 mr-1" />
                  Back
                </Button>
              )}
              {isLast ? (
                <Button size="sm" onClick={onFinish}>
                  <Check className="h-3.5 w-3.5 mr-1" />
                  Finish
                </Button>
              ) : (
                <Button size="sm" onClick={onNext}>
                  Next
                  <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
